/**
 * The app's own account of what it could not do.
 *
 * Everything diagnosed this week was reconstructed from side effects: a
 * duplicate row, a driver's GPS going silent, a count that did not move. One driver
 * Camargo started his route at 15:36, his phone stopped reporting at 15:42, and
 * nothing anywhere says why. That is the gap this closes.
 *
 * Three rules, learned the hard way:
 *
 *  1. Reporting a failure must never itself fail loudly. This is diagnostics;
 *     it cannot be allowed to break a delivery. Everything here swallows its
 *     own errors and returns void.
 *
 *  2. It must survive no signal — which is exactly when the interesting
 *     failures happen. Reports are held in localStorage and sent on the next
 *     opportunity, so a freeze in a basement still reaches us from the car park.
 *
 *  3. It must never block. Sending happens in the background and nothing in the
 *     app waits for it.
 */
import { api } from '../api/client';

export type ErrorKind =
  | 'camera_denied' | 'camera_failed' | 'decoder_failed'
  | 'request_timeout' | 'upload_failed'
  | 'gps_timeout' | 'gps_denied'
  /*
   * The session died mid-shift and the queue could not send. Worth a row of its
   * own: one driver's whole day was lost to this and the only trace was 17
   * anonymous 401s in an API log. We should hear about the next one by name.
   */
  | 'auth_expired'
  /* The stop a queued submission belonged to no longer exists. */
  | 'task_gone'
  /* A face check was due but the server could not be asked. See below. */
  | 'face_check_unavailable'
  /* 17 Oct: a face scan kept on the phone for lack of signal, and one that
     could not be sent within two days. */
  | 'face_scan_saved_offline' | 'face_scan_dropped' | 'face_scan_required'
  | 'crash' | 'unhandled'
  /*
   * The two kinds that catch what nothing else does.
   *
   * 'died' — the app stopped existing without an error: iOS reclaiming the tab,
   * the browser being killed, a hard reload out of a wedged page. Nothing in
   * the dying session can report it, so the NEXT launch reports it, with the
   * trail of what the driver was doing. This is the only visibility we have
   * into the failure the fleet complains about most.
   *
   * 'slow' — it worked, eventually. No error, and until now no trace at all,
   * which is why "the scanner takes too long" stayed an opinion for a week
   * instead of a number.
   */
  | 'died' | 'slow'
  /* A driver who was asked to install the app and could not, or would not. */
  | 'install_skipped'
  /*
   * Measurements, not failures (3 Oct) -- see diag.ts. Every "app problems"
   * count on the dashboard leaves 'diag_*' out.
   */
  | 'diag_storage' | 'diag_queue' | 'diag_sync' | 'diag_background' | 'diag_selfcheck'
  | 'diag_permission' | 'diag_offline'
  /* One record per stop: every step, with seconds (stopLog.ts). Home or tab, once a day. */
  | 'diag_stop' | 'diag_install' | 'diag_repeat'
  /*
   * The driver was shown "your phone is not sure where it is": no usable
   * location at the moment they pressed Complete. Until 3 Oct this left no
   * trace, so a driver saying it happened could be neither believed nor
   * disproved.
   */
  | 'no_location';

interface Pending {
  kind: ErrorKind;
  screen?: string;
  detail?: string;
  occurredAt: string;
}

const KEY = 'jw.errors';
const MAX_HELD = 40;

/*
 * The build, so "every phone since Tuesday" is answerable.
 *
 * Just the TAG. The build injects the whole image reference --
 * "us-west1-docker.pkg.dev/jwed-pod/jw/jw-driver-pwa:v83" -- and only the last
 * part carries information. Sending the full 53 characters made every report
 * fail a 40-character check on the server and be thrown away for four days.
 * The server truncates now too; this is the other half, so the value that
 * arrives is "v83" rather than a registry path cut off mid-word.
 */
const RAW_VERSION: string =
  (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_APP_VERSION
  ?? 'dev';
const APP_VERSION: string = (RAW_VERSION.split(':').pop() ?? RAW_VERSION).slice(0, 40);

/**
 * Which SCREEN, not which record.
 *
 * `location.pathname` on a stop is "/stop/<uuid>" -- 42 characters, which also
 * failed the server check, so crashes on the one screen that matters most were
 * the ones most reliably lost. The id is in the delivery tables already; what
 * a fault report needs is the KIND of screen, and grouping by it is how a
 * pattern becomes visible at all.
 */
function screenName(path: string): string {
  const first = path.split('/').filter(Boolean)[0];
  return (first ?? 'home').slice(0, 40);
}

function read(): Pending[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Pending[]) : [];
  } catch {
    return [];
  }
}

function write(items: Pending[]): void {
  try {
    // Oldest dropped first: a crash loop must not fill the phone's storage.
    localStorage.setItem(KEY, JSON.stringify(items.slice(-MAX_HELD)));
  } catch {
    /* private mode, or full. Diagnostics are not worth an exception. */
  }
}

let sending = false;

/** Push whatever is held. Safe to call often; silent about its own failures. */
export async function flushErrors(): Promise<void> {
  if (sending) return;
  const items = read();
  if (items.length === 0) return;
  sending = true;
  try {
    const left: Pending[] = [];
    for (const item of items) {
      try {
        await api.reportError({
          kind: item.kind,
          screen: item.screen ?? null,
          detail: item.detail ?? null,
          userAgent: navigator.userAgent,
          appVersion: APP_VERSION,
          online: navigator.onLine,
          occurredAt: item.occurredAt,
        });
      } catch {
        // Still no signal. Keep it and everything after it, in order.
        left.push(item);
      }
    }
    write(left);
  } finally {
    sending = false;
  }
}

/**
 * Record a failure. Returns immediately; sending is somebody else's problem.
 *
 * `detail` is for us, not for the driver — a message, a status code, the name
 * of the thing that would not start. It must never carry anything about a
 * patient or a delivery: this is about the software, and it is read by staff.
 */
export function reportError(
  kind: ErrorKind,
  screen?: string,
  detail?: unknown,
): void {
  const text = detail instanceof Error ? detail.message
    : typeof detail === 'string' ? detail
    : detail === undefined ? undefined
    : (() => { try { return JSON.stringify(detail); } catch { return String(detail); } })();

  const items = read();
  items.push({
    kind,
    screen,
    detail: text?.slice(0, 500),
    occurredAt: new Date().toISOString(),
  });
  write(items);

  // Opportunistic. If there is no signal it simply stays held.
  void flushErrors();
}

/**
 * Catch what nobody remembered to catch.
 *
 * A crash on a stop screen currently leaves a white page and a driver with no
 * idea what happened, and leaves us with nothing at all. These two handlers are
 * the difference between "the app froze" as a rumour and as a timestamped line.
 */
/*
 * What the driver was doing just before a crash. session.ts registers this
 * (a getter, not an import, so the two modules do not depend on each other).
 */
let stepsProvider: (() => string) | null = null;
export function setStepsProvider(f: () => string): void { stepsProvider = f; }

/** Our code, or something else running in the page, judged by the script's address. */
function originOf(file: string | undefined): string {
  if (!file) return 'unknown';
  try {
    const u = new URL(file, location.href);
    return u.origin === location.origin ? 'app' : u.origin.slice(0, 60);
  } catch { return 'unknown'; }
}

/** Opened inside another app's built-in browser, which injects its own scripts. */
function hostApp(): string | null {
  const ua = navigator.userAgent;
  if (/GSA\//.test(ua)) return 'google-app';
  if (/FBAN|FBAV/.test(ua)) return 'facebook';
  if (/Instagram/.test(ua)) return 'instagram';
  if (/CriOS/.test(ua)) return 'chrome-ios';
  return null;
}

/*
 * 4 Oct: crashes used to be recorded as their one-line message only. Five were
 * just "Script error." -- which is what a browser says when the crash came from
 * a script that is NOT ours (an extension, a content blocker, another app's
 * built-in browser) and so hides the details. Our own bundle is served from our
 * own address with crossorigin, so a crash in OUR code always carries its file,
 * line and stack. Recording those, plus where it came from and the driver's
 * last steps, tells us whose crash it was.
 */
export function installCrashReporting(): void {
  window.addEventListener('error', (e) => {
    // A script, style or image that failed to LOAD arrives here too, with a
    // target element and no message.
    const target = e.target as (HTMLElement & { src?: string; href?: string }) | null;
    if (target && target !== (window as unknown) && target.tagName) {
      reportError('crash', screenName(location.pathname), {
        msg: 'failed to load', tag: target.tagName.toLowerCase(),
        src: (target.src || target.href || '').replace(location.origin, '').slice(0, 120),
        steps: stepsProvider?.() ?? null,
      });
      return;
    }
    const outside = e.message === 'Script error.' && !e.filename;
    reportError('crash', screenName(location.pathname), {
      msg: (e.message ?? '').slice(0, 120),
      from: outside ? 'outside-app' : originOf(e.filename),
      file: e.filename ? e.filename.replace(location.origin, '').slice(0, 80) : null,
      line: e.lineno || null,
      col: e.colno || null,
      stack: (e.error as Error | undefined)?.stack?.slice(0, 200) ?? null,
      host: hostApp(),
      steps: stepsProvider?.() ?? null,
    });
  }, true);
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason as Error | undefined;
    reportError('unhandled', screenName(location.pathname), {
      msg: (r?.message ?? String(e.reason)).slice(0, 160),
      name: r?.name ?? null,
      stack: r?.stack?.slice(0, 200) ?? null,
      host: hostApp(),
      steps: stepsProvider?.() ?? null,
    });
  });
  // Signal returning, or the driver coming back to the app, are both good
  // moments to try again.
  window.addEventListener('online', () => { void flushErrors(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushErrors();
  });
}
